'use client';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createContext, useEffect, useState, useCallback, useContext, Component, useRef } from 'react';
import ErrorBoundary from '@sbt-web/error-boundary';
import CN from 'classnames';
import Markdown from 'react-markdown';
import { ButtonLink, Button, FullModal, DeviceWidth, Badge, Icon, IconSize, Palette, IconName } from '@sbt-web/ui';
import { Pulse } from '@sbt-web/tracking';
import { getActiveVoBanners, getUnreadMessages } from '@sbt-web/network/client';
import { getCookie } from '@sbt-web/utils';
import { NetError, HTTPStatusCode } from '@sbt-web/network/types';

const initialValue = {};
const LinkContext = createContext(initialValue);

/**
 * Validates that a string is neither undefined nor empty.
 * @param s The string to check
 */
const isStringSet = (s) => s !== undefined && s !== '';

var classes$8 = {"image":"index-module_image__kyHgf","image-wrapper":"index-module_image-wrapper__YB0GI","text":"index-module_text__WRiIc","main-text":"index-module_main-text__5koRd","secondary-text":"index-module_secondary-text__0pX0z"};

const Modal = ({ placement, onClose, isOpen = false, modalTitle, modalMobileImage, modalDesktopImage, modalText, modalFooter, modalCloseButtonText, modalLinkButtonText, modalLinkButtonLink, pulseInstance, }) => {
    const onCloseModal = () => {
        pulseInstance?.queueEvent({
            type: 'Click',
            object: {
                type: 'UIElement',
                elementType: 'button',
                '@id': `sdrn:subito:page:modal-info:element:${placement}-modal-close`,
                label: `${placement} Modal`,
            },
        });
        onClose();
    };
    useEffect(() => {
        if (isOpen) {
            pulseInstance?.queueEvent({
                type: 'View',
                object: {
                    type: 'UIElement',
                    '@id': `sdrn:subito:page:modal-info:element:view-${placement}-modal`,
                    label: `${placement} Modal`,
                },
            });
        }
    }, [isOpen, placement, pulseInstance]);
    const actionButton = isStringSet(modalLinkButtonLink) && isStringSet(modalLinkButtonText) ? (jsx(ButtonLink, { href: modalLinkButtonLink, children: modalLinkButtonText }, "sticky-banner-modal-action-button")) : null;
    const closeButton = isStringSet(modalCloseButtonText) ? (jsx(Button, { onClick: onCloseModal, children: modalCloseButtonText }, "sticky-banner-modal-close-button")) : null;
    const actions = [];
    if (actionButton !== null) {
        actions.push(actionButton);
    }
    if (closeButton !== null) {
        actions.push(closeButton);
    }
    return (jsxs(FullModal, { open: isOpen, onClose: onCloseModal, title: modalTitle, actions: actions, children: [jsx("figure", { className: classes$8['image-wrapper'], children: jsxs("picture", { children: [jsx("source", { media: `(max-width: ${DeviceWidth.tablet.max})`, srcSet: modalMobileImage }), jsx("source", { media: `(min-width: ${DeviceWidth.desktop.min})`, srcSet: modalDesktopImage }), jsx("img", { className: classes$8['image'], src: modalMobileImage, alt: "" })] }) }), jsx("div", { className: CN(classes$8.text, classes$8['main-text']), children: jsx(Markdown, { children: modalText }) }), isStringSet(modalFooter) ? (jsx("footer", { className: CN(classes$8.text, classes$8['secondary-text']), children: jsx(Markdown, { children: modalFooter }) })) : null] }));
};

var ownClasses = {"stars-container":"stars-module_stars-container__IGpIG","star":"stars-module_star__-sbye","star-big":"stars-module_star-big__mFCAU","star-small":"stars-module_star-small__88Nrt","star-very-small":"stars-module_star-very-small__6yrWB","star-small-left":"stars-module_star-small-left__r0grf","star-big-left":"stars-module_star-big-left__TCXw2","star-big-right":"stars-module_star-big-right__xOCH2","star-small-right":"stars-module_star-small-right__luDU-","star-very-small-right":"stars-module_star-very-small-right__CTEg5"};

const Star = ({ classes, color, width, height, }) => (jsx("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 14 12", width: width, height: height, className: CN(...classes), children: jsx("path", { fill: color, d: "M6.55.16c0 1.54.7 3.02 1.92 4.12a6.99 6.99 0 0 0 4.63 1.7c-1.74 0-3.4.62-4.63 1.7a5.52 5.52 0 0 0-1.92 4.12 5.5 5.5 0 0 0-1.92-4.11A6.99 6.99 0 0 0 0 5.99a7 7 0 0 0 4.63-1.71A5.52 5.52 0 0 0 6.55.16Z" }) }));
const Binary = () => (jsxs("div", { className: ownClasses['stars-container'], children: [jsx(Star, { classes: [
                ownClasses.star,
                ownClasses['star-small'],
                ownClasses['star-small-left'],
            ], color: "var(--sbt-primary-color-l2)", width: 7, height: 6 }), jsx(Star, { classes: [
                ownClasses.star,
                ownClasses['star-big'],
                ownClasses['star-big-left'],
            ], color: "var(--sbt-primary-color-d2)", width: 14, height: 12 })] }));
const Ternary = () => (jsxs("div", { className: ownClasses['stars-container'], children: [jsx(Star, { classes: [
                ownClasses.star,
                ownClasses['star-big'],
                ownClasses['star-big-right'],
            ], color: "var(--sbt-primary-color-d2)", width: 14, height: 12 }), jsx(Star, { classes: [
                ownClasses.star,
                ownClasses['star-small'],
                ownClasses['star-small-right'],
            ], color: "var(--sbt-primary-color-l2)", width: 7, height: 6 }), jsx(Star, { classes: [
                ownClasses.star,
                ownClasses['star-very-small'],
                ownClasses['star-very-small-right'],
            ], color: "var(--sbt-primary-color-l2)", width: 3.5, height: 3 })] }));

var classes$7 = {"sticky-banner":"index-module_sticky-banner__0mfJe","sticky-banner--text-only":"index-module_sticky-banner--text-only__CLenw","badge":"index-module_badge__KqPt3","text":"index-module_text__Ic-c5"};

const StickyBanner$1 = ({ content, placement, categorySlug, pulseInstance, assetsBaseUrl, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { withStars = false, bannerBadge, bannerBadgeColor, bannerBadgeTextColor, bannerIcon, bannerText, bannerLink, bannerColor, bannerTextColor, modalTitle, modalDesktopImage, modalMobileImage, modalText, modalFooter, modalCloseButtonText, modalLinkButtonLink, modalLinkButtonText, } = content;
    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);
    const isModalBanner = isStringSet(modalTitle) &&
        isStringSet(modalDesktopImage) &&
        isStringSet(modalMobileImage) &&
        isStringSet(modalText) &&
        (isStringSet(modalCloseButtonText) ||
            (isStringSet(modalLinkButtonLink) && isStringSet(modalLinkButtonText)));
    const isLinkBanner = !isModalBanner && isStringSet(bannerLink);
    const handleClick = () => {
        const pulseId = categorySlug
            ? `sdrn:subito:${placement}:${categorySlug}:banner:element:promo-banner`
            : `sdrn:subito:${placement}:banner:element:promo-banner`;
        const pulseLabel = categorySlug
            ? `Click on Promo Banner ${categorySlug}`
            : 'Click on Promo Banner';
        pulseInstance?.queueEvent({
            type: 'Click',
            object: {
                type: 'UIElement',
                '@id': pulseId,
                label: pulseLabel,
            },
        });
        if (isModalBanner) {
            setIsOpen(true);
        }
    };
    const badgeCustomStyles = bannerBadgeColor || bannerBadgeTextColor
        ? {
            ...(bannerBadgeColor && { backgroundColor: bannerBadgeColor }),
            ...(bannerBadgeTextColor && { color: bannerBadgeTextColor }),
        }
        : undefined;
    const bannerContent = (jsxs(Fragment, { children: [withStars ? jsx(Binary, {}) : null, isStringSet(bannerBadge) ? (jsx(Badge, { classes: [classes$7['badge']], style: badgeCustomStyles, children: bannerBadge })) : null, isStringSet(bannerIcon) ? (jsx("img", { src: `${assetsBaseUrl}/static/product-marketing/${bannerIcon}`, width: 16, height: 16, alt: "" })) : null, jsx("div", { className: classes$7['text'], children: jsx(Markdown, { children: bannerText }) }), withStars ? jsx(Ternary, {}) : null] }));
    const sharedStyle = bannerColor || bannerTextColor
        ? {
            ...(bannerColor && { backgroundColor: bannerColor }),
            ...(bannerTextColor && { color: bannerTextColor }),
        }
        : undefined;
    const sharedClassName = CN(classes$7['sticky-banner'], 'theme-redCorporate');
    const ariaLabel = 'Banner promozionale';
    if (!isLinkBanner && !isModalBanner) {
        return (jsx("section", { "aria-label": ariaLabel, className: CN(sharedClassName, classes$7['sticky-banner--text-only']), style: sharedStyle, children: bannerContent }));
    }
    if (isLinkBanner) {
        return (jsx("section", { "aria-label": ariaLabel, children: jsx("a", { href: bannerLink, target: "_blank", rel: "noopener noreferrer", onClick: handleClick, className: sharedClassName, style: sharedStyle, children: bannerContent }) }));
    }
    if (isModalBanner) {
        return (jsxs("section", { "aria-label": ariaLabel, children: [jsx("button", { onClick: handleClick, "aria-haspopup": "dialog", className: sharedClassName, style: sharedStyle, children: bannerContent }), jsx(Modal, { placement: placement, onClose: handleClose, isOpen: isOpen, modalTitle: modalTitle, modalMobileImage: `${assetsBaseUrl}/static/product-marketing/${modalMobileImage}`, modalDesktopImage: `${assetsBaseUrl}/static/product-marketing/${modalDesktopImage}`, modalText: modalText, modalFooter: modalFooter, modalCloseButtonText: modalCloseButtonText, modalLinkButtonText: modalLinkButtonText, modalLinkButtonLink: modalLinkButtonLink, pulseInstance: pulseInstance })] }));
    }
    return null;
};

/*
 * Utility function to check if the sticky banner supports the placement
 */
const isSupportedPage = (placement) => ['home', 'listing', 'adview'].includes(placement);
/*
 * Utility function to check if the returned Optimizely configuration is valid
 */
const isNonEmptyConfig = (config) => !!config && typeof config === 'object' && Object.keys(config).length > 0;
const StickyBanner = ({ content, placement, categorySlug, pulseInstance, assetsBase, }) => {
    if (!isSupportedPage(placement) || !content || !isNonEmptyConfig(content)) {
        return null;
    }
    return (jsx(ErrorBoundary, { children: jsx(StickyBanner$1, { content: content, placement: placement, categorySlug: categorySlug, pulseInstance: pulseInstance, assetsBaseUrl: assetsBase }) }));
};

const ProfileIcon = () => (jsx("svg", { width: "24", height: "25", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M22 12.5c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10 10 4.477 10 10ZM6.707 18.499A7.97 7.97 0 0 0 12 20.5a7.97 7.97 0 0 0 5.293-2.001 5.503 5.503 0 0 0-10.586 0Zm-1.476-1.733a8 8 0 1 1 13.538 0 7.518 7.518 0 0 0-3.756-3.635 4 4 0 1 0-6.026 0 7.518 7.518 0 0 0-3.756 3.634ZM10 10.5a2 2 0 1 0 4 0 2 2 0 0 0-4 0Z", fill: "#717e8f" }) }));
const OrderIcon = () => (jsx("svg", { width: "24", height: "25", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "m12.216 4.524.14.042 9 3.428.076.033.144.084.103.083.052.052.09.112.077.13.038.087.033.106.025.141.006.107v9.142a1 1 0 0 1-.532.884l-.112.05-9.04 3.444-.184.042-.152.01-.112-.01-.113-.021-.111-.035-9-3.43a1 1 0 0 1-.636-.81L2 18.07V8.88l.006-.059L2 8.93l.002-.063.027-.176.037-.117.047-.107.059-.098.079-.102.114-.11.1-.072.112-.062.103-.042 8.964-3.414a1 1 0 0 1 .457-.06l.115.018ZM20 17.38v-7.058l-.666.238-.567.21c-.263.099-.511.195-.74.288l-.362.15a17.81 17.81 0 0 0-.166.07L17.5 13.5a1 1 0 0 1-1.993.117L15.5 13.5v-1.407l-2.5.952v7.004l7-2.668ZM4 10.379v7.003l7 2.667v-7.003l-7-2.667Zm3.5-2.096-1.692.645L12 11.287l1.691-.644L7.5 8.283Zm4.499-1.714-1.691.644 6.147 2.343.388-.171c.137-.059.282-.119.434-.18l.25-.1.56-.216-6.088-2.32Z", fill: "#717e8f" }) }));
const AdIcon = () => (jsx("svg", { width: "24", height: "25", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5 2.5h14a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-16a2 2 0 0 1 2-2Zm0 2v16h14v-16H5Zm2 1h10a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1Zm1 2v3h8v-3H8Zm-2 7a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm1 2a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2H7Z", fill: "#717e8f" }) }));
const SettingsIcon = () => (jsxs("svg", { width: "24", height: "25", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14 12.5a2 2 0 1 0-4 0 2 2 0 0 0 4 0Zm2 0a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm3.502 5.609 1.486-2.47-2.103-1.168a6.689 6.689 0 0 0 .304-2.923 7.011 7.011 0 0 0-.3-1.372L21 8.893l-1.467-2.325-2.1 1.285a6.802 6.802 0 0 0-1.007-.903c-.44-.311-.851-.575-1.36-.785-.2-.082-.755-.328-1.209-.403V3.5h-3.628v2.262c-.453.094-.823.21-1.25.387-.51.21-.873.458-1.313.77-.377.268-.737.539-1.052.87L4.49 6.569 3 8.893l2.111 1.283c-.148.43-.247.995-.3 1.435a6.53 6.53 0 0 0 .301 2.875l-2.108 1.16L4.48 18.11l2.1-1.16c.315.332.633.635 1.011.902.44.312.881.576 1.39.786.428.176.793.314 1.246.407V21.5h3.628v-2.46c.605-.125 1.003-.32 1.204-.403.509-.21.84-.474 1.28-.785a7.09 7.09 0 0 0 1.045-.903l2.117 1.16Zm-2.006 1.374c.01-.007-.37.266-.49.348a6.104 6.104 0 0 1-1.15.64V21.5a2 2 0 0 1-2 2h-3.628a2 2 0 0 1-2-2v-1.01a8.872 8.872 0 0 1-1.925-1.103l-.854.473a2 2 0 0 1-2.683-.722L1.29 16.676a2 2 0 0 1 .75-2.781l.792-.436a8.504 8.504 0 0 1 .023-2.314l-.893-.543a2 2 0 0 1-.645-2.79l1.492-2.324a2 2 0 0 1 2.682-.653l.914.527.105-.075c.689-.488 1.095-.735 1.707-.987.004-.002.012-.8.012-.8a2 2 0 0 1 2-2h3.628a2 2 0 0 1 2 2v.828c.602.25 1.095.544 1.726.99l.073.054.832-.51a2 2 0 0 1 2.736.638l1.468 2.325a2 2 0 0 1-.652 2.777l-.886.538.022.167a8.687 8.687 0 0 1-.003 2.085c-.002.019.787.499.787.499a2 2 0 0 1 .743 2.779l-1.486 2.47a2 2 0 0 1-2.675.723l-.893-.49c-.05.037-.1.074-.152.11Z", fill: "#717e8f" }), jsx("mask", { id: "a", maskUnits: "userSpaceOnUse", x: "0", y: "1", width: "23", height: "23", children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14 12.5a2 2 0 1 0-4 0 2 2 0 0 0 4 0Zm2 0a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm3.502 5.609 1.486-2.47-2.103-1.168a6.689 6.689 0 0 0 .304-2.923 7.011 7.011 0 0 0-.3-1.372L21 8.893l-1.467-2.325-2.1 1.285a6.802 6.802 0 0 0-1.007-.903c-.44-.311-.851-.575-1.36-.785-.2-.082-.755-.328-1.209-.403V3.5h-3.628v2.262c-.453.094-.823.21-1.25.387-.51.21-.873.458-1.313.77-.377.268-.737.539-1.052.87L4.49 6.569 3 8.893l2.111 1.283c-.148.43-.247.995-.3 1.435a6.53 6.53 0 0 0 .301 2.875l-2.108 1.16L4.48 18.11l2.1-1.16c.315.332.633.635 1.011.902.44.312.881.576 1.39.786.428.176.793.314 1.246.407V21.5h3.628v-2.46c.605-.125 1.003-.32 1.204-.403.509-.21.84-.474 1.28-.785a7.09 7.09 0 0 0 1.045-.903l2.117 1.16Zm-2.006 1.374c.01-.007-.37.266-.49.348a6.104 6.104 0 0 1-1.15.64V21.5a2 2 0 0 1-2 2h-3.628a2 2 0 0 1-2-2v-1.01a8.872 8.872 0 0 1-1.925-1.103l-.854.473a2 2 0 0 1-2.683-.722L1.29 16.676a2 2 0 0 1 .75-2.781l.792-.436a8.504 8.504 0 0 1 .023-2.314l-.893-.543a2 2 0 0 1-.645-2.79l1.492-2.324a2 2 0 0 1 2.682-.653l.914.527.105-.075c.689-.488 1.095-.735 1.707-.987.004-.002.012-.8.012-.8a2 2 0 0 1 2-2h3.628a2 2 0 0 1 2 2v.828c.602.25 1.095.544 1.726.99l.073.054.832-.51a2 2 0 0 1 2.736.638l1.468 2.325a2 2 0 0 1-.652 2.777l-.886.538.022.167a8.687 8.687 0 0 1-.003 2.085c-.002.019.787.499.787.499a2 2 0 0 1 .743 2.779l-1.486 2.47a2 2 0 0 1-2.675.723l-.893-.49c-.05.037-.1.074-.152.11Z", fill: "#fff" }) })] }));
const FavoritesIcon = () => (jsx("svg", { width: "24", height: "25", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M16 3.5c3.552 0 6 2.448 6 6 0 4.098-3.212 8.024-9.479 11.853a1 1 0 0 1-1.043 0C5.213 17.523 2 13.598 2 9.5c0-3.552 2.448-6 6-6 1.397 0 2.728.682 4 1.987C13.272 4.182 14.603 3.5 16 3.5Zm4 6c0-2.448-1.552-4-4-4-.981 0-2.058.673-3.22 2.125a1 1 0 0 1-1.56 0C10.057 6.173 8.98 5.5 8 5.5c-2.448 0-4 1.552-4 4 0 3.136 2.619 6.433 8 9.823 5.381-3.39 8-6.687 8-9.823Z", fill: "#717e8f" }) }));
const SearchesIcon = () => (jsxs("svg", { width: "24", height: "25", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17.5 16.562a6.955 6.955 0 0 1-1.158.536 1 1 0 0 1-.67-1.884A4.996 4.996 0 0 0 19 10.5a5 5 0 0 0-8.727-3.334 1 1 0 0 1-1.49-1.334 7 7 0 0 1 10.823 8.86l3.101 3.1a1 1 0 0 1-1.414 1.415l-3.1-3.1c-.22.164-.452.316-.692.455ZM15 12.388c0-2.106-1.722-3.888-3.824-3.888A3.835 3.835 0 0 0 8.5 9.583 3.835 3.835 0 0 0 5.824 8.5C3.721 8.5 2 10.273 2 12.377c0 2.217 1.16 4.155 3.08 5.885.512.461 1.049.881 1.634 1.294.235.166.706.483.923.63l.113.076a1.298 1.298 0 0 0 1.5 0l.112-.076c.216-.145.689-.464.924-.63a17.491 17.491 0 0 0 1.633-1.294C13.84 16.532 15 14.598 15 12.388Zm-4.42 4.388c-.446.403-.923.775-1.447 1.146-.146.103-.408.28-.633.433-.225-.152-.487-.33-.633-.433a15.447 15.447 0 0 1-1.448-1.146C4.88 15.388 4 13.921 4 12.377c0-1.013.839-1.877 1.824-1.877.791 0 1.483.5 1.73 1.223.308.902 1.584.902 1.892 0a1.822 1.822 0 0 1 1.73-1.223c.982 0 1.824.872 1.824 1.888 0 1.536-.878 3-2.42 4.388Z", fill: "#717e8f" }), jsx("mask", { id: "a", maskUnits: "userSpaceOnUse", x: "2", y: "3", width: "21", height: "18", children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17.5 16.562a6.955 6.955 0 0 1-1.158.536 1 1 0 0 1-.67-1.884A4.996 4.996 0 0 0 19 10.5a5 5 0 0 0-8.727-3.334 1 1 0 0 1-1.49-1.334 7 7 0 0 1 10.823 8.86l3.101 3.1a1 1 0 0 1-1.414 1.415l-3.1-3.1c-.22.164-.452.316-.692.455ZM15 12.388c0-2.106-1.722-3.888-3.824-3.888A3.835 3.835 0 0 0 8.5 9.583 3.835 3.835 0 0 0 5.824 8.5C3.721 8.5 2 10.273 2 12.377c0 2.217 1.16 4.155 3.08 5.885.512.461 1.049.881 1.634 1.294.235.166.706.483.923.63l.113.076a1.298 1.298 0 0 0 1.5 0l.112-.076c.216-.145.689-.464.924-.63a17.491 17.491 0 0 0 1.633-1.294C13.84 16.532 15 14.598 15 12.388Zm-4.42 4.388c-.446.403-.923.775-1.447 1.146-.146.103-.408.28-.633.433-.225-.152-.487-.33-.633-.433a15.447 15.447 0 0 1-1.448-1.146C4.88 15.388 4 13.921 4 12.377c0-1.013.839-1.877 1.824-1.877.791 0 1.483.5 1.73 1.223.308.902 1.584.902 1.892 0a1.822 1.822 0 0 1 1.73-1.223c.982 0 1.824.872 1.824 1.888 0 1.536-.878 3-2.42 4.388Z", fill: "#fff" }) })] }));
const MessagesIcon = () => (jsx("svg", { width: "24", height: "24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M6 19H5a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-7.667L9.2 20.6A2 2 0 0 1 6 19Zm13-2a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3v2l2.667-2H19ZM8 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm5-1a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z", fill: "#717e8f" }) }));

function Link(props) {
    const linkProp = useContext(LinkContext);
    const { children, ...anchorProps } = props;
    return (jsx("a", { ...anchorProps, href: anchorProps.href, onClick: (e) => {
            anchorProps.onClick?.(e);
            linkProp.onClick?.(anchorProps.href);
        }, children: children }));
}

function logout(userID, pulse) {
    pulse.queueEvent({
        type: 'Logout',
        object: {
            type: 'Account',
            id: `sdrn:subito:user:${userID}`,
        },
    });
}

var styles = {"menu-list":"index-module_menu-list__ZiPDz","item":"index-module_item__vvcgL","item-link":"index-module_item-link__deMmy","logout":"index-module_logout__YKhaV","with-notice":"index-module_with-notice__nfq-c"};

const getMenuItems = (urlPrefixes, userId, isMobile, isLoggedUser, pulseInstance, notices = []) => [
    {
        href: `${urlPrefixes.reservedArea}/transazioni/lista`,
        label: 'I tuoi ordini',
        icon: jsx(OrderIcon, {}),
        disabled: false,
        showNotice: notices.includes('orders'),
    },
    {
        href: `${urlPrefixes.baseURL}/utente/${userId}`,
        label: 'Il tuo profilo',
        icon: jsx(ProfileIcon, {}),
        disabled: isMobile && !isLoggedUser,
        showNotice: notices.includes('profile'),
    },
    {
        href: `${urlPrefixes.reservedArea}/annunci`,
        label: 'I tuoi annunci',
        icon: jsx(AdIcon, {}),
        disabled: false,
        showNotice: notices.includes('manageads'),
    },
    {
        href: `${urlPrefixes.reservedArea}/messaggi${!isMobile ? '?from=dropdown' : ''}`,
        label: 'Messaggi',
        icon: jsx(MessagesIcon, {}),
        disabled: isMobile,
        showNotice: notices.includes('messages'),
    },
    {
        href: `${urlPrefixes.reservedArea}/preferiti${!isMobile ? '?from=dropdown' : ''}`,
        label: 'Preferiti',
        icon: jsx(FavoritesIcon, {}),
        disabled: false,
        showNotice: notices.includes('favorites'),
    },
    {
        href: `${urlPrefixes.reservedArea}/ricerche${!isMobile ? '?from=dropdown' : ''}`,
        label: 'Ricerche salvate',
        icon: jsx(SearchesIcon, {}),
        disabled: false,
        showNotice: notices.includes('searches'),
    },
    {
        href: `${urlPrefixes.reservedArea}/garage`,
        label: 'Il tuo garage',
        icon: (jsx(Icon, { name: "Car", fillColor: Palette.NeutralGrey2, iconSize: IconSize.MD })),
        endIcon: jsx(Badge, { themed: true, children: "New" }),
        disabled: false,
        showNotice: notices.includes('garage'),
    },
    {
        href: `${urlPrefixes.reservedArea}/impostazioni`,
        label: 'Impostazioni',
        icon: jsx(SettingsIcon, {}),
        disabled: isMobile && !isLoggedUser,
        showNotice: notices.includes('settings'),
    },
    {
        href: `${urlPrefixes.reservedArea}/logout`,
        label: 'Esci',
        disabled: isMobile,
        class: 'logout',
        onClick: trackLogout(userId, pulseInstance),
        showNotice: false,
    },
];
function trackLogout(userId, pulseInstance) {
    return () => {
        const pulse = pulseInstance || new Pulse();
        logout(userId, pulse);
        return true;
    };
}
const MenuList = ({ urlPrefixes, isMobile, userId, isLoggedUser, classes, pulseInstance, notices, }) => {
    const menuItems = getMenuItems(urlPrefixes, userId, isMobile, isLoggedUser, pulseInstance, notices);
    const list = menuItems.filter((menuItem) => !menuItem.disabled);
    return (jsx("menu", { className: CN(styles['menu-list'], classes), children: list.map((item, index) => (jsx("li", { className: CN(styles['item']), children: jsxs(Link, { href: item.href, onClick: item.onClick, className: CN(styles['item-link'], item.class ? styles[item.class] : '', { [styles['with-notice']]: item.showNotice }), children: [item.icon, jsx("p", { className: CN(item.label === 'Esci' ? 'semibold' : 'book', 'body-text', styles['item-label']), children: item.label }), item.endIcon] }) }, index))) }));
};

var classes$6 = {"notification-badge":"index-module_notification-badge__0fBtF","mini":"index-module_mini__1VV2G"};

const maxCounterValue = 99;
const NotificationBadge = (props) => {
    const { count, mini = false } = props;
    const classMini = classes$6['mini'];
    return !count ? (jsx(Fragment, {})) : (jsx("div", { className: CN(classes$6['notification-badge'], { [classMini]: mini }), children: count <= maxCounterValue ? count : `${maxCounterValue}+` }));
};

var classes$5 = {"user-menu-block":"index-module_user-menu-block__oeFMo","user-menu":"index-module_user-menu__XC7QN","username-block":"index-module_username-block__OTZXs","username":"index-module_username__sD--k","notification-counter-container":"index-module_notification-counter-container__BsK0d","banner-counter-container":"index-module_banner-counter-container__8hEf5","dropdown-menu":"index-module_dropdown-menu__4S31H","icon-arrow":"index-module_icon-arrow__awXZ2"};

const DropdownMenu = ({ user, unreadMessages = 0, unreadBanners, urlPrefixes, pulseInstance, }) => {
    const { name, id } = user;
    return (jsxs("div", { className: classes$5['user-menu-block'], children: [jsxs("div", { className: classes$5['user-menu'], children: [unreadMessages > 0 && (jsx("div", { className: classes$5['notification-counter-container'], children: jsx(NotificationBadge, { count: unreadMessages, mini: false }) })), unreadBanners && (jsx("div", { className: classes$5['banner-counter-container'] })), jsxs("div", { className: classes$5['username-block'], children: [jsx("div", { className: classes$5['username'], children: jsx("span", { className: "body-text semibold", children: name }) }), jsx("img", { src: `${urlPrefixes.assetsBase}/static/icons/cactus/arrow-down.svg`, width: "16", height: "16", alt: "", className: classes$5['icon-arrow'], crossOrigin: "anonymous" })] })] }), jsx(MenuList, { isLoggedUser: true, isMobile: false, userId: id, urlPrefixes: urlPrefixes, classes: [classes$5['dropdown-menu']], pulseInstance: pulseInstance })] }));
};

var classes$4 = {"accedi-ora":"index-module_accedi-ora__i6nP1"};

const LOGIN_TOOLTIP_KEY = 'login-tooltip-disabled';
function useTooltip(isUserLogged, section) {
    const [tooltipDisabled, setTooltipDisabled] = useState(true);
    useEffect(() => {
        if (section === 'listing' && !isUserLogged) {
            try {
                const loginTooltipAlreadyViewed = sessionStorage.getItem(LOGIN_TOOLTIP_KEY) === 'true';
                if (!loginTooltipAlreadyViewed) {
                    sessionStorage.setItem(LOGIN_TOOLTIP_KEY, 'true');
                }
                setTooltipDisabled(loginTooltipAlreadyViewed);
            }
            catch {
                console.error('Could not access sessionStorage to check the configuration of the tooltip.');
            }
        }
    }, [section, isUserLogged]);
    return !tooltipDisabled;
}
function LoginTooltip() {
    return (jsxs("div", { className: classes$4['accedi-ora'], children: ["Buoni affari ti stanno aspettando!\u00A0", jsx("strong", { children: "Accedi ora" })] }));
}

var classes$3 = {"header-wrapper":"index-module_header-wrapper__DdDr3","top-link-container":"index-module_top-link-container__1kIBU","header":"index-module_header__r5EmJ","top-menu-container":"index-module_top-menu-container__IpvvE","not-logged":"index-module_not-logged__ovJr4","login":"index-module_login__-lvcn","button-add":"index-module_button-add__pXNeG","button":"index-module_button__wrBiG","btn-add-ad":"index-module_btn-add-ad__56z7X","header-icon-add-ad":"index-module_header-icon-add-ad__tFQDk"};

const magazineUTMMedium = (section) => {
    switch (section) {
        case 'adview':
            return 'addetailheader';
        case 'listing':
            return 'listingheader';
        case 'home':
            return 'hpheader';
        default:
            return 'otherheader';
    }
};
const TopNavigation = ({ section }) => {
    const topNavigationLinks = [
        {
            href: `https://www.subito.it/magazine/#?utm_source=sbtclassified&utm_medium=${magazineUTMMedium(section)}`,
            label: 'Magazine',
        },
        {
            href: 'https://info.subito.it/per-i-privati.htm',
            label: 'Consigli per la vendita',
        },
        {
            href: 'https://impresapiu.subito.it/shops?xtfrom=header_menu',
            label: 'Negozi e Aziende',
        },
        {
            href: 'https://aziende.subito.it/',
            label: 'Subito per le Aziende',
        },
        {
            href: 'https://assistenza.subito.it/hc/it',
            label: 'Assistenza',
        },
        {
            href: 'https://areariservata.subito.it/ricerche',
            label: 'Ricerche salvate',
        },
        {
            href: 'https://areariservata.subito.it/preferiti',
            label: 'Preferiti',
        },
    ];
    return (jsx("nav", { className: classes$3['top-link-container'], children: jsx("ul", { children: topNavigationLinks.map((topNavigationLink, key) => {
                return (jsx("li", { children: jsx(Link, { href: topNavigationLink.href, children: jsx("span", { className: "body-text small", children: topNavigationLink.label }) }) }, key));
            }) }) }));
};
const Logo = ({ theme = 'corporate', baseURL = '', assetsBase = '', }) => (jsx(Link, { href: baseURL, title: "Subito.it", "aria-label": "Torna alla pagina principale", children: jsx("img", { src: `${assetsBase}/static/logos/${theme || 'corporate'}.svg`, width: "230", height: "36", alt: "", className: `${theme || 'corporate'}-logo`, crossOrigin: "anonymous" }) }));
const NotLoggedMenu = ({ section, reservedArea = '', tooltipEnabled = false, }) => {
    const loginTooltipQuery = tooltipEnabled ? '?login_tooltip=true' : '';
    return (jsxs("div", { className: classes$3['not-logged'], children: [jsxs(Link, { className: classes$3['login'], href: `${reservedArea}/login_form${loginTooltipQuery}`, children: [jsx("p", { className: "body-text semibold", children: "Accedi" }), section === 'listing' && tooltipEnabled ? jsx(LoginTooltip, {}) : null] }), jsx(Link, { className: classes$3['signup'], href: `${reservedArea}/form`, children: jsx("p", { className: "body-text", children: "Registrati" }) })] }));
};
const Plus$1 = () => (jsx("svg", { width: "24", height: "24", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M8 5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3H8zm0-2h8a5 5 0 0 1 5 5v8a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V8a5 5 0 0 1 5-5zm5 8h3a1 1 0 0 1 0 2h-3v3a1 1 0 0 1-2 0v-3H8a1 1 0 0 1 0-2h3V8a1 1 0 0 1 2 0v3z" }) }));
const AddAdButton = ({ onClick, addAdLink }) => {
    const buttonStyle = [
        classes$3['button'],
        classes$3['btn_large'],
        classes$3['btn-add-ad'],
    ].join(' ');
    return (jsx("div", { className: classes$3['button-add'], children: jsx("a", { href: addAdLink, onClick: onClick, children: jsxs("div", { className: buttonStyle, children: [jsx("div", { className: classes$3['header-icon-add-ad'], children: jsx(Plus$1, {}) }), jsx("span", { className: "button-text", children: "Inserisci annuncio" })] }) }) }));
};

/**
 * Header is responsive subito header component
 */
const Header$1 = ({ theme = 'corporate', unreadMessages = 0, unreadBanners, urlPrefixes, section, categorySlug, user = {
    id: '',
    logged: false,
    name: '',
}, tooltipEnabled = false, onClickHandlers, addAdLink, pulseInstance, stickyBannerContent, }) => {
    const [isLogged, setIsLogged] = useState();
    const { baseURL, assetsBase, reservedArea } = urlPrefixes;
    useEffect(() => {
        setIsLogged(user.logged);
    }, [user.logged]);
    return (jsxs("header", { className: classes$3['header-wrapper'], children: [jsx(StickyBanner, { content: stickyBannerContent, placement: section ?? '', categorySlug: categorySlug, pulseInstance: pulseInstance, assetsBase: urlPrefixes.assetsBase }), jsx(TopNavigation, { section: section }), jsxs("div", { className: classes$3['header'], children: [jsx(Logo, { theme: theme, baseURL: baseURL, assetsBase: assetsBase }), jsxs("div", { className: classes$3['top-menu-container'], children: [isLogged === true ? (jsx(DropdownMenu, { user: user, unreadMessages: unreadMessages, unreadBanners: unreadBanners, urlPrefixes: urlPrefixes })) : null, isLogged === false ? (jsx(NotLoggedMenu, { section: section, reservedArea: reservedArea, tooltipEnabled: tooltipEnabled })) : null] }), jsx(AddAdButton, { onClick: () => onClickHandlers?.adButton?.(), addAdLink: addAdLink })] })] }));
};
Header$1.displayName = 'Header';

const Chat = () => (jsx("svg", { width: "24", height: "24", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M6 19H5a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-7.667L9.2 20.6A2 2 0 0 1 6 19zm13-2a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3v2l2.667-2H19zM9 12a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z", fillRule: "evenodd" }) }));
const Lens = () => (jsx("svg", { width: "24", height: "24", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M15.05 16.463a7.5 7.5 0 1 1 1.414-1.414l4.243 4.244a1 1 0 0 1-1.414 1.414l-4.244-4.244zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z", fillRule: "evenodd" }) }));
const Plus = () => (jsx("svg", { width: "24", height: "24", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M8 5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3H8zm0-2h8a5 5 0 0 1 5 5v8a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V8a5 5 0 0 1 5-5zm5 8h3a1 1 0 0 1 0 2h-3v3a1 1 0 0 1-2 0v-3H8a1 1 0 0 1 0-2h3V8a1 1 0 0 1 2 0v3z", fillRule: "evenodd" }) }));
const Snap = () => (jsx("svg", { width: "24", height: "24", xmlns: "http://www.w3.org/2000/svg", children: jsx("g", { fillRule: "nonzero", children: jsx("path", { d: "M20.604 12.12l-2.244.838c-.322.12-.693-.025-.823-.324l-3.491-7.99c-.13-.3.027-.643.35-.763l2.244-.838c.322-.121.693.024.824.323l3.49 7.99c.13.3-.027.643-.35.763M14.84 13.335l-1.505 1.505a.552.552 0 0 1-.778 0L5.16 7.443a.552.552 0 0 1 0-.778L6.665 5.16a.551.551 0 0 1 .777 0l7.398 7.397a.552.552 0 0 1 0 .778M12.957 18.36l-.838 2.244c-.12.323-.463.48-.762.35l-7.99-3.491c-.3-.13-.445-.501-.325-.824l.84-2.244c.12-.322.463-.48.76-.35l7.992 3.492c.299.13.444.5.323.824" }) }) }));
const User = () => (jsx("svg", { width: "24", height: "24", xmlns: "http://www.w3.org/2000/svg", children: jsxs("g", { fill: "none", fillRule: "evenodd", children: [jsx("path", { fill: "#AEB9C6", d: "M12 3a5 5 0 013.824 8.22C17.96 11.89 20 14.068 20 17.678v.775c0 1.082-.539 1.856-1.376 2.257-.461.221-.9.291-1.24.291H6.617c-.339 0-.778-.07-1.24-.29C4.539 20.307 4 19.533 4 18.451v-.775c0-3.61 2.04-5.788 4.176-6.456A5 5 0 0112 3zm0 2a3 3 0 100 6 3 3 0 000-6zm-2.423 8c-.332 0-1.205.287-1.891.8C6.626 14.594 6 15.827 6 17.677v.775c0 .274.067.37.24.453.142.069.309.095.375.095h10.77c.067 0 .233-.026.376-.095.172-.082.239-.179.239-.453v-.775c0-1.85-.626-3.083-1.686-3.877-.686-.513-1.559-.8-1.89-.8H9.576z", id: "a" }), jsxs("g", { transform: "translate(14)", children: [jsx("rect", { id: "c", x: "0", y: "0", width: "12", height: "12", rx: "6", fill: "#000", filter: "url(#b)" }), jsx("rect", { id: "c", x: "0", y: "0", width: "12", height: "12", rx: "6", fillOpacity: "0", fill: "#FFF" }), jsx("rect", { fill: "#FFF", width: "12", height: "12", rx: "6" }), jsx("rect", { x: ".5", y: ".5", width: "11", height: "11", rx: "5.5" })] }), jsxs("g", { transform: "translate(16 2)", children: [jsx("rect", { id: "e", x: "0", y: "0", width: "8", height: "8", rx: "4", fill: "#000", filter: "url(#d)" }), jsx("rect", { id: "e", x: "0", y: "0", width: "8", height: "8", rx: "4", fillOpacity: "0", fill: "#FFF" }), jsx("rect", { fill: "#F1F1F1", width: "8", height: "8", rx: "4" }), jsx("rect", { fill: "#F9423A", width: "8", height: "8", rx: "4" })] })] }) }));
const UserCircle = () => (jsx("svg", { width: "24", height: "24", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M6.707 17.999A7.97 7.97 0 0 0 12 20a7.97 7.97 0 0 0 5.293-2.001 5.503 5.503 0 0 0-10.586 0zm-1.476-1.733a7.517 7.517 0 0 1 3.756-3.635 4 4 0 1 1 6.026 0 7.517 7.517 0 0 1 3.756 3.634 8 8 0 1 0-13.538 0zM12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z", fillRule: "evenodd" }) }));
const UserSkeleton = () => (jsx("svg", { width: "24", height: "24", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M11.9998871,3 C14.761274,3 17,5.2386508 17,8.00016933 C17,9.22709757 16.557948,10.3508585 15.8244317,11.2208825 C17.9593961,11.8887628 20,14.0674914 20,17.6774229 L20,18.4515424 C20,19.5336262 19.4614937,20.3082436 18.6241146,20.7091127 C18.1625359,20.930079 17.7235888,21.0001051 17.3845383,21 L6.61623493,21 C6.27708249,21.0001949 5.83785602,20.9302238 5.37596716,20.7091245 C4.53854492,20.3082631 4,19.5336448 4,18.4515424 L4,17.6774229 C4,14.067472 6.04060964,11.8887558 8.17577236,11.2207129 C7.44199698,10.3508882 7,9.22712434 7,8.00016933 C7,5.23859057 9.2384476,3 11.9998871,3 Z M11.9998871,5 C10.3430457,5 9,6.34313147 9,8.00016933 C9,9.65683826 10.3431283,11 11.9998871,11 C13.6566369,11 15,9.65673439 15,8.00016933 C15,6.34323535 13.6567195,5 11.9998871,5 Z M9.5769691,13 C9.24502787,13 8.37207569,13.2866804 7.6859541,13.8004913 C6.62642857,14.5939306 6,15.8274514 6,17.6774229 L6,18.4515424 C6,18.7259655 6.06725554,18.8227028 6.23950074,18.905154 C6.38244589,18.9735797 6.54853377,19 6.61469396,19 L17.3854132,19 C17.4515575,19 17.617615,18.9735826 17.7605315,18.9051658 C17.9327486,18.8227223 18,18.7259841 18,18.4515424 L18,17.6774229 C18,15.8274542 17.3735736,14.5939321 16.3140551,13.8004875 C15.627952,13.2866837 14.7550206,13 14.4231449,13 L9.5769691,13 Z", fillRule: "evenodd" }) }));

var classes$2 = {"header-container":"index-module_header-container__lxbI8","fluid-container":"index-module_fluid-container__3n68n","header-link":"index-module_header-link__pcEnw","visible":"index-module_visible__5uywn","header-icon":"index-module_header-icon__W6am6","link-title":"index-module_link-title__mnAgF","active":"index-module_active__3IQoN","fluid-column":"index-module_fluid-column__xe0jo","badge-container":"index-module_badge-container__QL6Zy","tooltip-container":"index-module_tooltip-container__h52Uo"};

/**
 * The number of pixels to wait before triggering a state change
 */
const scrollThreshold = 3;
/**
 * The number of pixels from the top before triggering any state change
 */
const scrollInitialOffset = 45;
const GridItem = (props) => {
    return jsx("div", { className: classes$2['fluid-column'], children: props.children });
};
const HeaderLink = (props) => {
    const { active, href, onClick, title, children } = props;
    const scrollTop = (e) => {
        if (active) {
            e.preventDefault();
            window.scroll({ top: 0, behavior: 'smooth' });
        }
    };
    const classActive = classes$2['active'];
    return (jsxs(Link, { className: CN(classes$2['header-link'], { [classActive]: active }), href: active ? '#' : href, onClick: onClick || scrollTop, children: [jsx("div", { className: classes$2['header-icon'], children: children }), jsx("p", { className: classes$2['link-title'], children: title })] }));
};
class HeaderMobile extends Component {
    state = {
        visible: true,
        isLogged: undefined,
    };
    #scrollData = {
        offsetY: 0,
        threshold: 0,
        prevDirection: '',
    };
    constructor(props) {
        super(props);
    }
    #scrollListener = () => {
        const currentOffsetY = window.scrollY;
        const { offsetY, prevDirection } = this.#scrollData;
        const currentDirection = offsetY > currentOffsetY ? 'up' : 'down';
        if (!currentOffsetY || currentOffsetY < scrollInitialOffset) {
            this.props.headerVisibilityCallback?.(true);
            this.setState({
                visible: true,
            });
            return;
        }
        if ((currentDirection === 'up' && prevDirection === 'up') ||
            (currentDirection === 'down' && prevDirection === 'down')) {
            ++this.#scrollData.threshold;
        }
        if (currentDirection !== prevDirection) {
            this.#scrollData.threshold = 0;
        }
        this.#scrollData.offsetY = currentOffsetY;
        this.#scrollData.prevDirection = currentDirection;
        if (this.#scrollData.threshold > scrollThreshold &&
            currentDirection === 'up') {
            this.props.headerVisibilityCallback?.(true);
            return this.setState({
                visible: true,
                isLogged: this.state.isLogged,
            });
        }
        this.props.headerVisibilityCallback?.(false);
        return this.setState({
            visible: false,
            isLogged: this.state.isLogged,
        });
    };
    componentDidMount() {
        window.addEventListener('scroll', this.#scrollListener, { passive: true });
        this.setState({
            visible: this.state.visible,
            isLogged: Boolean(this.props.user?.logged),
        });
    }
    componentDidUpdate(prevProps) {
        if (prevProps.user?.logged !== this.props.user?.logged) {
            this.setState({
                visible: this.state.visible,
                isLogged: Boolean(this.props.user?.logged),
            });
        }
    }
    componentWillUnmount() {
        window.removeEventListener('scroll', this.#scrollListener);
    }
    render() {
        const { section, categorySlug, user, onUserTap, onUserTooltipTap, urlPrefixes, unreadMessages, unreadBanners, tooltipEnabled = false, addAdLink, pulseInstance, stickyBannerContent, } = this.props;
        const { name } = user;
        const { visible, isLogged } = this.state;
        const UserIcon = isLogged || tooltipEnabled ? UserCircle : User;
        return (jsxs("div", { className: CN(classes$2['header-container'], {
                [classes$2['visible']]: visible,
            }), children: [jsx(StickyBanner, { content: stickyBannerContent, placement: section ?? '', categorySlug: categorySlug, pulseInstance: pulseInstance, assetsBase: urlPrefixes.assetsBase }), jsxs("div", { className: classes$2['fluid-container'], children: [jsx(GridItem, { children: jsx(HeaderLink, { href: urlPrefixes.baseURL, active: section === 'home', title: "Home", children: jsx(Snap, {}) }) }), jsx(GridItem, { children: jsx(HeaderLink, { href: `${urlPrefixes.baseURL}/annunci-italia/vendita/usato`, active: section === 'listing', title: "Cerca", children: jsx(Lens, {}) }) }), jsx(GridItem, { children: jsx(HeaderLink, { href: addAdLink, active: section === 'ad-insertion-homepage', title: "Vendi", onClick: () => {
                                    this.props.onClickHandlers?.adButton?.();
                                }, children: jsx(Plus, {}) }) }), jsxs(GridItem, { children: [jsx(HeaderLink, { href: `${urlPrefixes.reservedArea}/messaggi`, active: section === 'messages', title: "Messaggi", children: jsx(Chat, {}) }), unreadMessages > 0 && (jsx("div", { className: classes$2['badge-container'], children: jsx(NotificationBadge, { count: unreadMessages, mini: true }) }))] }), jsxs(GridItem, { children: [jsxs(HeaderLink, { onClick: onUserTap, active: section === 'reservedarea', title: isLogged === undefined ? '' : isLogged ? name : 'Entra', children: [isLogged === undefined ? null : isLogged ? (jsx(UserIcon, {})) : (jsx(UserSkeleton, {})), section === 'listing' &&
                                            visible &&
                                            !isLogged &&
                                            tooltipEnabled ? (jsx("div", { className: classes$2['tooltip-container'], onClick: onUserTooltipTap, onKeyDown: onUserTooltipTap, role: "button", tabIndex: 0, children: jsx(LoginTooltip, {}) })) : null] }), unreadBanners && (jsx("div", { className: classes$2['badge-container'], children: jsx(NotificationBadge, { count: 1, mini: true }) }))] })] })] }));
    }
}

var classes$1 = {"sidebar-container":"index-module_sidebar-container__czSgA","sidebar-right":"index-module_sidebar-right__hvyPB","visible":"index-module_visible__NTId-","sidebar-left":"index-module_sidebar-left__u5y6W","sidebar-header":"index-module_sidebar-header__BQAvP","close-button":"index-module_close-button__1JSPd","sidebar-content":"index-module_sidebar-content__vBHsJ","sidebar-overlay":"index-module_sidebar-overlay__LW4a0","header-text":"index-module_header-text__LOM6i"};

function Sidebar({ visible, from, onDismiss, children, user, }) {
    const classVisible = classes$1['visible'];
    const classFrom = classes$1[`sidebar-${from}`];
    const { name } = user;
    const logged = !!name;
    return (jsxs("section", { role: "dialog", "aria-modal": "true", children: [jsxs("div", { className: CN(classes$1['sidebar-container'], { [classVisible]: visible }, classFrom), children: [jsxs("div", { className: classes$1['sidebar-header'], children: [jsx("h6", { className: CN('headline-6', classes$1['header-text']), children: logged ? name : 'Accedi' }), jsx(Button, { design: "text", icon: jsx(Icon, { iconSize: IconSize.SM, name: IconName.Close }), onClick: onDismiss, size: "large", classes: [classes$1['close-button']], "aria-label": "Chiudi il men\u00F9" })] }), jsx("div", { className: classes$1['sidebar-content'], children: children })] }), jsx("div", { className: CN(classes$1['sidebar-overlay'], {
                    [classVisible]: visible,
                }), onClick: onDismiss, "aria-hidden": true })] }));
}

var classes = {"sidebar-bottom":"index-module_sidebar-bottom__1flbJ","logout-link":"index-module_logout-link__VdwEW","actions-container":"index-module_actions-container__lVLoK","login__btn":"index-module_login__btn__-koW4","signup__copy":"index-module_signup__copy__3Z44Y"};

class HeaderMobileNavigation extends Component {
    constructor(props) {
        super(props);
        this.state = {
            tooltipClicked: false,
            isSidebarOpen: false,
        };
    }
    handleSidebarClose = (e) => {
        if (e) {
            e.preventDefault();
        }
        this.setState({
            isSidebarOpen: false,
        });
    };
    handleUserTap = (e) => {
        if (e) {
            e.preventDefault();
        }
        this.setState({
            isSidebarOpen: true,
        });
    };
    handleUserTooltipTap = () => {
        this.setState({
            tooltipClicked: true,
        });
    };
    renderAccedi() {
        const tooltipClicked = this.state.tooltipClicked;
        const trackingString = tooltipClicked ? '?login_tooltip=true' : '';
        return (jsxs("div", { className: classes['actions-container'], children: [jsx(ButtonLink, { href: `${this.props.urlPrefixes.reservedArea}/login_form${trackingString}`, classes: [classes['login__btn']], children: "Accedi" }), jsx(Link, { href: `${this.props.urlPrefixes.reservedArea}/form${trackingString}`, children: jsxs("p", { className: CN('body-text', 'small', classes['signup__copy']), children: ["Non hai ancora un account? ", jsx("strong", { children: "Registrati" })] }) })] }));
    }
    trackLogout = () => {
        const pulse = this.props.pulseInstance || new Pulse();
        logout(this.props.user?.id, pulse);
        return true;
    };
    render() {
        const { isSidebarOpen } = this.state;
        const { user, urlPrefixes } = this.props;
        return (jsxs(Fragment, { children: [jsx(HeaderMobile, { ...this.props, onUserTap: this.handleUserTap, onUserTooltipTap: this.handleUserTooltipTap }), jsxs(Sidebar, { from: "right", visible: isSidebarOpen, onDismiss: this.handleSidebarClose, user: user, children: [jsx(MenuList, { isMobile: true, urlPrefixes: urlPrefixes, userId: user.id, isLoggedUser: user.logged, pulseInstance: this.props.pulseInstance, notices: this.props.unreadBanners ? ['manageads'] : [] }), jsx("div", { className: classes['sidebar-bottom'], children: user.logged ? (jsx(Link, { className: classes['logout-link'], onClick: this.trackLogout, href: `${urlPrefixes.reservedArea}/logout`, children: jsx("span", { className: "body-text semibold", children: "Esci" }) })) : (this.renderAccedi()) })] })] }));
    }
}

function useUnreadBanners(logged, urlPrefixes) {
    const [hasUnreadBanners, setHasUnreadBanners] = useState(false);
    const checkUnreadBanners = async () => {
        const lastSeenBanner = getCookie('manageads.lastSeenBanner');
        try {
            const { body } = await getActiveVoBanners(urlPrefixes.hadesBase, 'RESERVEDAREA', 'MANAGEADS');
            setHasUnreadBanners(!!(body.banners[0]?.bannerId &&
                `${body.banners[0].bannerId}` !== lastSeenBanner));
        }
        catch {
            // if Agon fails we consider as "no banners"
        }
    };
    useEffect(() => {
        if (logged) {
            checkUnreadBanners();
        }
    }, [logged]);
    return hasUnreadBanners;
}

const useUnreadMessages = ({ baseUrl, userId, }) => {
    const [unreadCount, setUnreadCount] = useState();
    const intervalIdRef = useRef(null);
    const pollingTimeRef = useRef(null);
    const fetchUnreadCount = useCallback(async () => {
        if (document.visibilityState !== 'visible' || !userId)
            return;
        try {
            const { body: { unread, pollingTime }, } = await getUnreadMessages(baseUrl, userId);
            setUnreadCount(unread);
            if (pollingTimeRef.current !== pollingTime) {
                if (intervalIdRef.current) {
                    clearInterval(intervalIdRef.current);
                }
                if (!pollingTime) {
                    return;
                }
                pollingTimeRef.current = pollingTime;
                intervalIdRef.current = setInterval(fetchUnreadCount, pollingTime);
            }
        }
        catch (e) {
            //prevent continuous polling if the user is not logged in anymore
            if (e instanceof NetError &&
                e.cause.status === HTTPStatusCode.Unauthorized) {
                if (intervalIdRef.current) {
                    clearInterval(intervalIdRef.current);
                    intervalIdRef.current = null;
                }
                pollingTimeRef.current = null;
            }
            console.warn('[messaging unread messages] Failed to fetch unread messages:', e);
        }
    }, [baseUrl, userId]);
    useEffect(() => {
        const handleVisibilityChange = () => {
            const isVisible = document.visibilityState === 'visible';
            if (isVisible && !intervalIdRef.current && pollingTimeRef.current) {
                intervalIdRef.current = setInterval(fetchUnreadCount, pollingTimeRef.current);
            }
            if (!isVisible && intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchUnreadCount]);
    useEffect(() => {
        if (userId) {
            fetchUnreadCount();
        }
    }, [userId, fetchUnreadCount]);
    return unreadCount;
};

const Header = (props) => {
    const { user, urlPrefixes } = props;
    const logged = !!user.name;
    const properties = {
        ...props,
        user: {
            id: user.id,
            name: user.name,
            logged,
        },
    };
    const unreadBanners = useUnreadBanners(logged, urlPrefixes);
    const unreadMessagesDemerge = useUnreadMessages({
        baseUrl: urlPrefixes.hadesBase,
        userId: user.id,
    });
    const isTooltipEnabled = useTooltip(logged, props.section);
    const tooltipEnabled = props.tooltipEnabled
        ? props.tooltipEnabled
        : isTooltipEnabled;
    const addAdLink = `${urlPrefixes.baseURL}/vendere/`;
    return (jsxs(LinkContext.Provider, { value: { onClick: props.onClickHandlers?.onClickItem }, children: [jsx(HeaderMobileNavigation, { ...properties, unreadBanners: unreadBanners, unreadMessages: unreadMessagesDemerge ?? 0, tooltipEnabled: tooltipEnabled, addAdLink: addAdLink }), jsx(Header$1, { ...properties, unreadMessages: unreadMessagesDemerge ?? 0, unreadBanners: unreadBanners, tooltipEnabled: tooltipEnabled, addAdLink: addAdLink })] }));
};

export { Header as default };
//# sourceMappingURL=index.js.map
